/**
 * MailTemplate Integration
 * Patterns: Bridge between SDK and State, UI Feedback, Error Handling
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { useMailTemplateStore } from '@/store/mail-template.store';
import { useTenantStore } from '@/store/tenant.store';
import { ApiError } from '@repo/sdk';
import { Logger } from '@/lib/app-log.js';

export class MailTemplateIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Helper to get current tenant ID
     */
    _getTenantId() {
        return useTenantStore.getState().selectedTenantId;
    }

    /**
     * Standard error message extractor
     */
    _getErrorMessage(error) {
        if (error.isValidationError?.()) {
            return i18next.t('generalSystemMailTemplate:messages.validationError');
        }
        if (error.isNotFound?.()) {
            return i18next.t('generalSystemMailTemplate:messages.notFoundError');
        }
        if (error.isAuthError?.()) {
            return i18next.t('generalSystemMailTemplate:messages.authError');
        }
        if (error instanceof ApiError) return error.message;
        return error.message || i18next.t('generalSystemMailTemplate:messages.unexpectedError');
    }

    /**
     * Fetch MailTemplates with pagination, sorting and filtering
     */
    async fetchMailTemplates(page = 1, pageSize = 10, search = '', sortBy = 'name', sortOrder = 'asc', _columnFilters = {}) {
        const store = useMailTemplateStore.getState();
        const tenantId = this._getTenantId();

        if (!tenantId) {
            Logger.warn('Tenant ID is missing, skipping fetch');
            return;
        }

        store.setLoading(true);
        store.setError(null);

        try {
            const response = await this.service.getAll({
                page,
                limit: pageSize,
                search,
                sortBy,
                sortOrder,
                // ...columnFilters // Backend compatible filters
            });

            const { data, pagination } = response;
            store.setMailTemplates(data || []);

            if (pagination) {
                store.setPagination({
                    currentPage: pagination.page || page,
                    itemsPerPage: pagination.pageSize || pageSize,
                    totalItems: pagination.total || 0,
                    totalPages: pagination.totalPages || 0,
                    hasNextPage: (pagination.page < pagination.totalPages),
                    hasPreviousPage: (pagination.page > 1)
                });
            }

            // Extract unique values for filters
            if (response.data) {
                const uniqueNames = [...new Set(response.data.map(d => d.name).filter(Boolean))].sort();
                store.setUniqueValues({
                    name: uniqueNames
                });
            }

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Create a new MailTemplate
     */
    async createMailTemplate(data) {
        const store = useMailTemplateStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.create(data);
            toast.success(i18next.t('generalSystemMailTemplate:messages.createSuccess'));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update an existing MailTemplate
     */
    async updateMailTemplate(id, data) {
        const store = useMailTemplateStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('generalSystemMailTemplate:messages.updateSuccess'));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete a MailTemplate
     */
    async deleteMailTemplate(id) {
        const store = useMailTemplateStore.getState();
        store.setLoading(true);

        try {
            await this.service.delete(id);
            toast.success(i18next.t('generalSystemMailTemplate:messages.deleteSuccess'));
            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete MailTemplates
     */
    async bulkDeleteMailTemplates(ids) {
        const store = useMailTemplateStore.getState();
        store.setLoading(true);

        try {
            await this.service.bulkDelete(ids);
            toast.success(i18next.t('generalSystemMailTemplate:messages.bulkDeleteSuccess'));
            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }
}
